/**
 * Cost Register: a program's ledger of found, cited prices. Each line is reviewed
 * (Approve / Exclude for the IGCE, Edit, Dismiss); only approved lines reach the IGCE.
 * A record of what was found, never a projection. Ported from the agent prototype's
 * RegisterSurface.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ChevronDown, ChevronRight, Download, FileUp, ListChecks, Loader2, Plus, RefreshCw } from 'lucide-react'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import JobProgressCard from '../../components/jobs/JobProgressCard'
import type { Job } from '../../hooks/useJobPoll'
import {
  type CapturedIn,
  type IgceStatus,
  type LineFields,
  PRICE_BASIS_LABELS,
  type PriceBasis,
  RECURRENCES,
  type RegisterItem,
  type RegisterSummary,
  type Verification,
  costRegisterApi,
  formatMoney,
} from '../../services/costRegister'
import RegisterIntake from './RegisterIntake'

const FIELD = 'w-full px-3 py-2 rounded-md border border-input bg-background text-foreground text-sm'
const humanize = (s: string) => s.replace(/_/g, ' ')
const MODULES: CapturedIn[] = ['market_research', 'acquisition_strategy', 'marketview', 'manual']
const VERIFICATIONS: Verification[] = ['not_found', 'unverified', 'verified']
const IGCE_STATUSES: IgceStatus[] = ['pending', 'approved', 'excluded']

const IGCE_BADGE: Record<IgceStatus, { label: string; variant: 'approved' | 'warning' | 'default' }> = {
  approved: { label: 'IGCE: approved', variant: 'approved' },
  pending: { label: 'IGCE: pending review', variant: 'warning' },
  excluded: { label: 'IGCE: excluded', variant: 'default' },
}

const EMPTY_LINE: LineFields = {
  description: '', category: 'hardware', amount: '', unit_price: '', quantity: '', unit_of_measure: '',
  recurrence: 'unknown', price_basis: 'user_entered', source_citation: '', notes: '', rationale: '',
}

/** Blank money / quantity inputs go to the API as null. */
function toPayload(f: LineFields): Partial<LineFields> {
  const num = (v: string | null) => (v === null || v.trim() === '' ? null : v.trim())
  return { ...f, amount: num(f.amount), unit_price: num(f.unit_price), quantity: num(f.quantity) }
}

function fromItem(i: RegisterItem): LineFields {
  const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
  return {
    description: i.description, category: i.category, amount: str(i.amount), unit_price: str(i.unit_price),
    quantity: str(i.quantity), unit_of_measure: i.unit_of_measure, recurrence: i.recurrence,
    price_basis: i.price_basis, source_citation: i.source_citation, notes: i.notes, rationale: i.rationale,
  }
}

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}

function LineForm({ value, onChange, categories, showRationale }: {
  value: LineFields
  onChange: (v: LineFields) => void
  categories: Record<string, string>
  showRationale?: boolean
}) {
  const set = (k: keyof LineFields) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange({ ...value, [k]: e.target.value })
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Description" className="sm:col-span-2">
          <input className={FIELD} value={value.description} onChange={set('description')} />
        </Field>
        <Field label="Category">
          <select className={FIELD} value={value.category} onChange={set('category')}>
            {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
        <Field label="Amount (total)">
          <input className={FIELD} type="number" min="0" step="any" value={value.amount ?? ''} onChange={set('amount')} />
        </Field>
        <Field label="Unit price">
          <input className={FIELD} type="number" min="0" step="any" value={value.unit_price ?? ''} onChange={set('unit_price')} />
        </Field>
        <Field label="Quantity">
          <input className={FIELD} type="number" min="0" step="any" value={value.quantity ?? ''} onChange={set('quantity')} />
        </Field>
        <Field label="Unit (seat, node…)">
          <input className={FIELD} value={value.unit_of_measure} onChange={set('unit_of_measure')} />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Recurrence">
          <select className={FIELD} value={value.recurrence} onChange={set('recurrence')}>
            {RECURRENCES.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
          </select>
        </Field>
        <Field label="Price basis">
          <select className={FIELD} value={value.price_basis} onChange={set('price_basis')}>
            {(Object.keys(PRICE_BASIS_LABELS) as PriceBasis[]).map((b) => (
              <option key={b} value={b}>{PRICE_BASIS_LABELS[b]}</option>
            ))}
          </select>
        </Field>
        <Field label="Citation (URL or document and page)">
          <input className={FIELD} value={value.source_citation} onChange={set('source_citation')} />
        </Field>
      </div>
      <Field label={value.price_basis === 'estimated' ? 'Notes (required for an estimate)' : 'Notes'}>
        <input className={FIELD} value={value.notes} onChange={set('notes')} />
      </Field>
      {showRationale && (
        <Field label="Rationale (why this belongs in the register / IGCE)">
          <input className={FIELD} value={value.rationale} onChange={set('rationale')} />
        </Field>
      )}
    </div>
  )
}

function RegisterRow({ item, categories, onChanged }: {
  item: RegisterItem
  categories: Record<string, string>
  onChanged: () => Promise<void>
}) {
  const [open, setOpen] = useState(item.needs_review)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<LineFields>(() => fromItem(item))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const noPrice = item.verification_status === 'not_found' || item.line_total === null

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setEditing(false)
      await onChanged()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const citation = item.source_citation
  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-accent/40"
      >
        {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
        <span
          aria-hidden
          className={`w-2 h-2 rounded-full shrink-0 ${item.needs_review || noPrice ? 'bg-warning' : 'bg-success'}`}
        />
        <span className="flex-1 min-w-0 truncate text-sm">{item.description}</span>
        {item.duplicate_of !== null && <Badge variant="warning">Possible duplicate</Badge>}
        {item.verification_status === 'not_found'
          ? <Badge variant="destructive">No price found</Badge>
          : <Badge variant={IGCE_BADGE[item.igce_status].variant}>{IGCE_BADGE[item.igce_status].label}</Badge>}
        <span className="w-28 text-right tabular-nums font-semibold text-sm">{formatMoney(item.line_total)}</span>
      </button>

      {open && (
        <div className="px-10 pb-4 space-y-3">
          <div className="rounded-md border border-border border-l-4 border-l-primary px-3 py-2 text-sm">
            <div className="text-xs font-medium text-muted-foreground mb-1">Why this is in the register</div>
            {item.rationale || 'No rationale was recorded for this line. Review its source before approving.'}
          </div>
          <div className="text-xs text-muted-foreground space-y-1">
            <div>
              {categories[item.category] ?? item.category}
              {item.product_name && <> · product: <span className="text-foreground">{item.product_name}</span></>}
              {item.vendor_name && <> · vendor: <span className="text-foreground">{item.vendor_name}</span></>}
              {' · '}{humanize(item.captured_in_module)} · {humanize(item.recurrence)}
              {item.unit_price !== null && (
                <> · {formatMoney(item.unit_price)}/{item.unit_of_measure || 'unit'}
                  {item.quantity !== null ? ` × ${Number(item.quantity)}` : ''}</>
              )}
            </div>
            <div>
              Basis: {PRICE_BASIS_LABELS[item.price_basis] ?? humanize(item.price_basis)} · Verification:{' '}
              <span className={item.verification_status === 'verified' ? 'text-success' : 'text-warning'}>
                {humanize(item.verification_status)}
              </span>
              {' · '}Confidence: {item.confidence}
              {item.as_of_date && ` · as of ${item.as_of_date}`}
            </div>
            <div className="break-all">
              Citation:{' '}
              {citation
                ? citation.startsWith('http')
                  ? <a href={citation} target="_blank" rel="noreferrer" className="underline text-foreground">{citation}</a>
                  : citation
                : 'None'}
            </div>
            {item.notes && <div>Notes: {item.notes}</div>}
          </div>

          {editing ? (
            <div className="space-y-3">
              <LineForm value={draft} onChange={setDraft} categories={categories} showRationale />
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} onClick={() => run(() => costRegisterApi.updateLine(item.id, toPayload(draft)))}>
                  Save
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => { setEditing(false); setDraft(fromItem(item)) }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {item.igce_status !== 'approved' && !noPrice && (
                <Button size="sm" variant="success" disabled={busy} title="Include this line in the IGCE"
                  onClick={() => run(() => costRegisterApi.review(item.id, 'approve'))}>
                  Approve for IGCE
                </Button>
              )}
              {item.igce_status !== 'excluded' && (
                <Button size="sm" variant="outline" disabled={busy}
                  title="Keep the line in the register but leave it out of the IGCE"
                  onClick={() => run(() => costRegisterApi.review(item.id, 'exclude'))}>
                  Exclude from IGCE
                </Button>
              )}
              <Button size="sm" variant="outline" disabled={busy} onClick={() => { setDraft(fromItem(item)); setEditing(true) }}>
                Edit
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} className="text-destructive"
                title="Remove from the register (the audit trail is kept)"
                onClick={() => run(() => costRegisterApi.review(item.id, 'dismiss'))}>
                Dismiss
              </Button>
            </div>
          )}
          {error && <div role="alert" className="text-sm text-destructive">{error}</div>}
        </div>
      )}
    </div>
  )
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`px-2.5 py-1 rounded-full border text-xs capitalize ${
        on ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  )
}

interface ProgramCostRegisterProps {
  /** Rendered inside another page (the workflow's costing step): no page padding. */
  embedded?: boolean
  programId?: string
}

const ProgramCostRegister = ({ embedded = false, programId: programIdProp }: ProgramCostRegisterProps = {}) => {
  const params = useParams<{ programId: string }>()
  const programId = programIdProp ?? params.programId
  const [items, setItems] = useState<RegisterItem[] | null>(null)
  const [summary, setSummary] = useState<RegisterSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)
  const [newLine, setNewLine] = useState<LineFields>(EMPTY_LINE)
  const [newCategory, setNewCategory] = useState('')
  // ✕ on the intake is remembered per program in this browser, so it doesn't reopen every visit.
  const dismissKey = `costRegister:intakeDismissed:${programId}`
  const [intakeOpen, setIntakeOpen] = useState<boolean | null>(null)  // null: not decided yet
  const fileInput = useRef<HTMLInputElement>(null)
  const [parseJob, setParseJob] = useState<Job | null>(null)
  const [parseNotes, setParseNotes] = useState<string[]>([])
  const [filters, setFilters] = useState<{ igce?: IgceStatus; module?: CapturedIn; verification?: Verification; category?: string }>({})

  const load = useCallback(async () => {
    if (!programId) return
    try {
      const [list, sum] = await Promise.all([costRegisterApi.list(programId), costRegisterApi.summary(programId)])
      setItems(list)
      setSummary(sum)
    } catch (e) {
      setError((e as Error).message)
    }
  }, [programId])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!summary || intakeOpen !== null) return
    let dismissed = false
    try { dismissed = localStorage.getItem(dismissKey) === '1' } catch { /* storage unavailable */ }
    setIntakeOpen(!summary.intake.done && !dismissed)
  }, [summary, intakeOpen, dismissKey])

  const closeIntake = () => {
    try { localStorage.setItem(dismissKey, '1') } catch { /* storage unavailable */ }
    setIntakeOpen(false)
  }

  const parseDocument = (file: File) => act('parse', async () => {
    try {
      const { job_id } = await costRegisterApi.parseDocument(programId!, file)
      const r = await costRegisterApi.waitForParse(job_id, setParseJob)
      setParseNotes(r.notes)
      return `Parsed ${file.name}: ${r.created} line${r.created === 1 ? '' : 's'} added for your review`
        + (r.dropped ? `; ${r.dropped} figure${r.dropped === 1 ? '' : 's'} dropped (no usable price).` : '.')
    } finally {
      setParseJob(null)
    }
  })

  /** Run a page-level action, then reload; errors and notices show in the banner. */
  const act = async (key: string, fn: () => Promise<string | void>) => {
    setBusy(key)
    setError(null)
    setNotice(null)
    setParseNotes([])
    try {
      const message = await fn()
      if (message) setNotice(message)
      await load()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const visible = useMemo(() => (items ?? []).filter((i) =>
    (!filters.igce || i.igce_status === filters.igce) &&
    (!filters.module || i.captured_in_module === filters.module) &&
    (!filters.verification || i.verification_status === filters.verification) &&
    (!filters.category || i.category === filters.category)), [items, filters])

  if (!programId) return null
  if (!items || !summary) {
    return error
      ? <div className="text-sm text-destructive p-4">Failed to load the Cost Register: {error}</div>
      : <div className="flex items-center gap-2 text-muted-foreground p-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading the Cost Register…</div>
  }

  const r = summary.rollups
  const categories = summary.categories
  const pending = r.completeness.igce.pending
  const toggle = <K extends keyof typeof filters>(k: K, v: (typeof filters)[K]) =>
    setFilters({ ...filters, [k]: filters[k] === v ? undefined : v })

  return (
    <div className={embedded ? 'space-y-6' : 'max-w-6xl mx-auto p-6 space-y-6'}>
      {intakeOpen && (
        <RegisterIntake
          programId={programId}
          components={summary.intake_components}
          onClose={closeIntake}
          onDone={(n) => {
            setIntakeOpen(false)
            return act('intake', async () => (n ? `Added ${n} planning line${n === 1 ? '' : 's'} from the guided questions.` : undefined))
          }}
        />
      )}
      <section className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Cost Register</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-3xl">
            A shared ledger of costs found in market research: a record of what was found, not a
            projection. Every captured line carries its citation and the reason it's here. Open a line,
            read its rationale, then <b>Approve for IGCE</b> or <b>Exclude</b>. Only approved lines reach the IGCE.
          </p>
        </div>

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ['Total', r.total],
            ['One-time', r.one_time_subtotal],
            ['Recurring', r.recurring_subtotal],
            ['Approved for IGCE', r.approved_total],
          ].map(([label, value]) => (
            <div key={label as string}>
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="text-xl font-semibold tabular-nums">{formatMoney(value as string)}</dd>
            </div>
          ))}
        </dl>
        <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">{r.completeness.statement}</div>
        {pending > 0 && (
          <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
            {pending} line{pending === 1 ? '' : 's'} await your IGCE review. Open each to read its rationale,
            then approve or exclude it.
          </div>
        )}
        {summary.intake.done && !summary.intake.skipped
          && (summary.intake.notes || summary.intake.expected_unpriced.length > 0) && (
          <div className="rounded-md border border-border px-3 py-2 text-sm space-y-1">
            <div className="text-xs font-medium text-muted-foreground">From the guided questions</div>
            {summary.intake.expected_unpriced.length > 0 && (
              <div>
                Expected, not yet priced: {summary.intake.expected_unpriced.join(', ')}. Add a line when you
                have a figure.
              </div>
            )}
            {summary.intake.notes && <div className="whitespace-pre-line">Notes: {summary.intake.notes}</div>}
          </div>
        )}
        <p className="text-xs text-muted-foreground">{r.label}</p>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={!!busy} title="The IGCE includes only lines you've approved"
            onClick={() => act('igce', () => costRegisterApi.export(programId, 'igce'))}>
            <Download /> Generate IGCE
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('csv', () => costRegisterApi.export(programId, 'csv'))}>
            <Download /> Export CSV
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => act('xlsx', () => costRegisterApi.export(programId, 'xlsx'))}>
            <Download /> Export XLSX
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy}
            title="Pull in unit and system costs for shortlisted products, and the PM's estimate"
            onClick={() => act('capture', async () => {
              const c = await costRegisterApi.captureFromMarketResearch(programId)
              return `Checked ${c.products} shortlisted product${c.products === 1 ? '' : 's'}: `
                + `${c.captured} new line(s), ${c.unchanged} unchanged, ${c.not_found} with no cost found`
                + (c.pm_estimate ? '; added the PM estimate.' : '.')
            })}>
            {busy === 'capture' ? <Loader2 className="animate-spin" /> : <RefreshCw />} Refresh from market research
          </Button>
          <Button size="sm" variant="outline" disabled={!!busy}
            title="Extract cited prices from a quote, price list or worksheet (PDF, DOCX, XLSX or CSV)"
            onClick={() => fileInput.current?.click()}>
            {busy === 'parse' ? <Loader2 className="animate-spin" /> : <FileUp />} Parse cost document
          </Button>
          <input ref={fileInput} type="file" hidden accept=".pdf,.docx,.xlsx,.csv" aria-label="Cost document"
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''  // the same file can be chosen again
              if (file) parseDocument(file)
            }} />
          {(!summary.intake.done || summary.intake.skipped) && (
            <Button size="sm" variant="outline" onClick={() => setIntakeOpen(true)}
              title="The cost components this program expects, with optional planning figures">
              <ListChecks /> Guided questions
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setShowAdd(!showAdd)} aria-expanded={showAdd}>
            <Plus /> Add line
          </Button>
        </div>
        {parseJob && <JobProgressCard job={parseJob} compact />}
        {notice && <div role="status" className="text-sm text-muted-foreground">{notice}</div>}
        {parseNotes.length > 0 && (
          <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5" aria-label="Notes from the parsed document">
            {parseNotes.map((n) => <li key={n}>{n}</li>)}
          </ul>
        )}
        {error && <div role="alert" className="text-sm text-destructive">{error}</div>}

        {showAdd && (
          <div className="border-t border-border pt-4 space-y-3">
            <LineForm value={newLine} onChange={setNewLine} categories={categories} />
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" disabled={!newLine.description.trim() || !!busy}
                onClick={() => act('add', async () => {
                  await costRegisterApi.addLine(programId, toPayload(newLine))
                  setNewLine({ ...EMPTY_LINE, category: newLine.category })
                  setShowAdd(false)
                })}>
                Add
              </Button>
              <span className="ml-auto flex gap-2">
                <input className={`${FIELD} w-48`} placeholder="New category…" aria-label="New category"
                  value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
                <Button size="sm" variant="outline" disabled={!newCategory.trim() || !!busy}
                  onClick={() => act('category', async () => {
                    const c = await costRegisterApi.addCategory(programId, newCategory)
                    setNewCategory('')
                    setNewLine({ ...newLine, category: c.key })
                  })}>
                  Add category
                </Button>
              </span>
            </div>
          </div>
        )}
      </section>

      <section className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border space-y-3">
          <h2 className="text-lg font-semibold">Line items ({visible.length})</h2>
          <div className="flex flex-wrap gap-1.5 items-center">
            {IGCE_STATUSES.map((s) => <Chip key={s} on={filters.igce === s} onClick={() => toggle('igce', s)}>IGCE {s}</Chip>)}
            {MODULES.map((m) => <Chip key={m} on={filters.module === m} onClick={() => toggle('module', m)}>{humanize(m)}</Chip>)}
            {VERIFICATIONS.map((v) => <Chip key={v} on={filters.verification === v} onClick={() => toggle('verification', v)}>{humanize(v)}</Chip>)}
            <select className="px-2.5 py-1 rounded-full border border-border bg-background text-xs" aria-label="Filter by category"
              value={filters.category ?? ''} onChange={(e) => setFilters({ ...filters, category: e.target.value || undefined })}>
              <option value="">All categories</option>
              {Object.entries(categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        {visible.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {items.length === 0
              ? 'No lines yet. Shortlist products in market research, then use "Refresh from market research", or add a line.'
              : 'No lines match these filters.'}
          </p>
        ) : (
          visible.map((i) => <RegisterRow key={i.id} item={i} categories={categories} onChanged={load} />)
        )}
      </section>
    </div>
  )
}

export default ProgramCostRegister
